import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, CheckCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUMBER = "+919270425109";
const EMAIL = "support@carecrony.com";

const serviceOptions = [
  "Patients & Elderly Care",
  "Home Nursing",
  "Nanny / Baby Sitting",
  "Other",
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", phone: "", service_required: "", message: "", bot_field: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) { setError("Please enter your name."); return; }
    if (!form.phone.trim() || form.phone.trim().length < 10) { setError("Please enter a valid phone number."); return; }
    if (!form.service_required) { setError("Please select a service."); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        service_required: form.service_required,
        message: form.message.trim(),
        bot_field: form.bot_field,
      });
      setSuccess(true);
      setForm({ name: "", phone: "", service_required: "", message: "", bot_field: "" });
    } catch {
      setError("Something went wrong. Please try calling us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 bg-gradient-to-br from-[#F0FDFA] to-[#F0F9FF]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="text-sm font-semibold text-[#14B8A6] uppercase tracking-wider font-body">Get In Touch</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#0F172A] tracking-tight mt-3 mb-6">
              Request a Call Back
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#64748B] font-body mb-10">
              Fill out the form or reach out directly. Our team responds within 2 hours.
            </p>

            <div className="space-y-4">
              <a href={`tel:${PHONE_NUMBER}`} data-testid="contact-phone-link"
                className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-100 hover:border-[#14B8A6]/20 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#14B8A6]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] font-body">Call Us</p>
                  <p className="font-heading text-base font-semibold text-[#0F172A]">+91 9270425109</p>
                </div>
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="contact-email-link"
                className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-100 hover:border-[#14B8A6]/20 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#14B8A6]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] font-body">Email</p>
                  <p className="font-heading text-base font-semibold text-[#0F172A]">{EMAIL}</p>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#14B8A6]" />
                </div>
                <div>
                  <p className="text-xs text-[#64748B] font-body">Serving</p>
                  <p className="font-heading text-base font-semibold text-[#0F172A]">Major Cities Across India</p>
                </div>
              </div>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            {success ? (
              <div data-testid="contact-form-success" className="bg-white rounded-3xl p-10 border border-slate-100 shadow-sm text-center h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[#14B8A6]/10 flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 text-[#14B8A6]" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-[#0F172A] mb-3">Thank You!</h3>
                <p className="text-[#64748B] font-body mb-6">We'll call you back within 2 hours.</p>
                <button onClick={() => setSuccess(false)} data-testid="contact-form-reset" className="text-sm font-semibold text-[#14B8A6] hover:underline font-body">
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-sm space-y-5">
                <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2">Request a Free Call Back</h3>
                <div className="absolute opacity-0 -z-10" aria-hidden="true">
                  <input type="text" name="bot_field" tabIndex={-1} autoComplete="off"
                    value={form.bot_field} onChange={(e) => setForm({ ...form, bot_field: e.target.value })} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0F172A] font-body mb-2">Your Name *</label>
                  <Input data-testid="contact-form-name" placeholder="e.g. Priya Sharma" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-12 rounded-xl border-slate-200 focus-visible:ring-[#14B8A6] font-body" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] font-body mb-2">Phone Number *</label>
                  <Input data-testid="contact-form-phone" type="tel" placeholder="+91 XXXXXXXXXX" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="h-12 rounded-xl border-slate-200 focus-visible:ring-[#14B8A6] font-body" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] font-body mb-2">Service Required *</label>
                  <Select value={form.service_required} onValueChange={(val) => setForm({ ...form, service_required: val })}>
                    <SelectTrigger data-testid="contact-form-service" className="h-12 rounded-xl border-slate-200 focus:ring-[#14B8A6] font-body">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] font-body mb-2">Message (optional)</label>
                  <Textarea data-testid="contact-form-message" placeholder="Tell us about your requirements..."
                    value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="min-h-[80px] rounded-xl border-slate-200 focus-visible:ring-[#14B8A6] font-body resize-none" />
                </div>

                {error && <p data-testid="contact-form-error" className="text-sm text-red-500 font-body">{error}</p>}

                <button type="submit" disabled={loading} data-testid="contact-form-submit"
                  className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full font-semibold bg-[#14B8A6] text-white hover:bg-[#0D9488] transition-all shadow-lg shadow-teal-500/20 font-body disabled:opacity-60">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Request Call Back</>}
                </button>

                <p className="text-xs text-center text-[#94A3B8] font-body">
                  By submitting, you agree to our{" "}
                  <Link to="/privacy-policy" className="font-semibold text-[#0F172A] hover:underline">Privacy Policy</Link>
                  {" "}and{" "}
                  <Link to="/terms-conditions" className="font-semibold text-[#0F172A] hover:underline">Terms &amp; Conditions</Link>
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
